// src/services/implementations/UsersServiceImpl.ts
import { UsersService } from '@/services/abstract/UsersService';
import type {
  UserEntity,
  UserUpsertDTO,
  UserId,
  InvitationResponse,
  CreatedUserResponse,
} from '@/types/users.types';
import type { FetcherService } from '@/services/abstract/FetcherService';
import { isNonEmptyString } from '@/utils/strings/strings';
import { sleepWithAbort } from '@/utils/async/sleepWithAbort';
import { userEndpoints } from '@/services/endpoints/user';
import type { UserStatus } from '@/types/organization.types';

const DEFAULT_POLL_INTERVAL_MS = 1500;
const DEFAULT_TIMEOUT_MS = 60_000;

export class UsersServiceImpl extends UsersService {
  constructor(private readonly fetcher: FetcherService) {
    super();
  }

  // --- CRUD ---
  async createUser(data: UserUpsertDTO): Promise<CreatedUserResponse> {
    return await this.fetcher.post<CreatedUserResponse>(
      userEndpoints.createUser,
      data
    );
  }

  async updateUser(id: UserId, data: UserUpsertDTO): Promise<UserEntity> {
    return await this.fetcher.patch<UserEntity>(
      userEndpoints.updateUserById(id),
      data
    );
  }

  async deleteUser(id: string): Promise<void> {
    await this.fetcher.del(userEndpoints.deleteUserById(id));
  }

  // --- Actions ---
  async toggleActiveUser(id: string, status: UserStatus): Promise<void> {
    await this.fetcher.patch(userEndpoints.updateUserStatus(id), { status });
  }

  async forceLogoutUser(id: string): Promise<void> {
    await this.fetcher.post(userEndpoints.forceUserLogout(id), {});
  }

  async forceLoginResetUser(id: string): Promise<void> {
    await this.fetcher.post(userEndpoints.forceUserLoginCredentialsReset(id), {});
  }

  // --- Invitation ---
  async getInvitationLink(userId: string): Promise<InvitationResponse> {
    return await this.fetcher.get<InvitationResponse>(
      userEndpoints.getUserInvitationLink(userId)
    );
  }

  async waitForInvitationLink(
    userId: string,
    options: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal } = {}
  ): Promise<InvitationResponse> {
    const intervalMs = options.intervalMs ?? DEFAULT_POLL_INTERVAL_MS;
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const { signal } = options;
    const deadline = Date.now() + timeoutMs;

    for (;;) {
      if ((signal?.aborted) ?? false) throw new DOMException('Aborted', 'AbortError');

      const response = await this.getInvitationLink(userId);
      if (isNonEmptyString(response.invitationLink)) return response;

      if (Date.now() > deadline) throw new Error('Timed out waiting for invitation link');
      await sleepWithAbort(intervalMs, signal);
    }
  }
}
